import { useState } from 'react'
import { useQuery } from 'react-query'
import axios from 'axios'
import useDeepCompareEffect from 'use-deep-compare-effect'
import { parseISO } from 'date-fns'
import { toPT } from './helpers'

type NextAvailableSlot = {
  locationId: string
  nextAvailableSlotTimes: {
    office: string | null
    video: string | null
  }
}

interface GetNextAvailableSlotResponse {
  data: { nextAvailableSlotsByLocation: Array<NextAvailableSlot> }
}

type ResponseSpecialtiesListItem = {
  key: string
  name: string
}

type ResponseProviderListItem = {
  providerEpicId: string
  locations: Array<{ name: string }>
}

type ResponseLocationListItem = {
  _id: string
  departmentId: string
  imageUrls: string[] | null
  specialtyAreaKeys?: string[]
  name: string
  phone: string
  address: string
  latitude: number
  longitude: number
  city: string
  zip: string
  locationUrl: string
}

type ResponseArrays = {
  providers: Array<ResponseProviderListItem>
  specialties: Array<ResponseSpecialtiesListItem>
  locations: Array<ResponseLocationListItem>
}

type Response = {
  MPC: ResponseArrays
  MTL: ResponseArrays
  WEL: ResponseArrays
}

export type LocationListItem = {
  id: string
  image: string
  isPediatrics: boolean
  name: string
  latitude: number
  longitude: number
  phone: string
  address: string
  city: string
  zip: string
  distance: number | null
  nextVideoSlot: Date | null
  nextOfficeSlot: Date | null
  zoom?: number
  locationUrl: string
}

export type LocationList = Array<LocationListItem>

// 1 hour
const cacheTime = 60 * 60 * 1000

// Ignore the Perlman Wellness locations (MTL/WEL), which share a department ID.
const IGNORE_LOCATIONS_ID = ['54202228']

const metaEndpoint = 'https://api.pocketdoc.io/api/schedule/multi-meta'
const slotsEndpoint =
  'https://api.perlmanclinic.app/api/schedule/next-available-slots/locations'

const DEFAULT_IMAGE =
  'http://www.carsaludable.com.ar/wp-content/uploads/2014/03/default-placeholder.png'

// Uses binary search to match a location to its slots, which are expected to
// come sorted in ascending order by location ID.
const findLocationSlots = (locationId: string, slots: Array<NextAvailableSlot>) => {
  let min = 0
  let max = slots.length - 1
  let guessIdx

  while (min <= max) {
    guessIdx = Math.floor((max + min) / 2)
    if (slots[guessIdx].locationId === locationId) {
      return slots[guessIdx]
    }
    if (slots[guessIdx].locationId < locationId) {
      min = guessIdx + 1
    } else {
      max = guessIdx - 1
    }
  }
  return null
}

const formatData = (
  slots: Array<NextAvailableSlot>,
  data?: {
    providers: Array<ResponseProviderListItem>
    specialties: Array<ResponseSpecialtiesListItem>
    locations: Array<ResponseLocationListItem>
  }
) =>
  data
    ? data.locations
      .map((loc) => {
        const locationSlots = findLocationSlots(loc.departmentId, slots)?.nextAvailableSlotTimes

        return {
          id: loc.departmentId,
          image:
            loc.imageUrls?.length && loc.imageUrls[0]
              ? loc.imageUrls[0]
              : DEFAULT_IMAGE,
          name: loc.name,
          isPediatrics: !!loc.specialtyAreaKeys?.includes('pediatrics'),
          latitude: loc.latitude,
          longitude: loc.longitude,
          phone: loc.phone,
          address: loc.address,
          city: loc.city,
          zip: loc.zip,
          distance: null,
          nextVideoSlot: locationSlots?.video ? toPT(parseISO(locationSlots.video)) : null,
          nextOfficeSlot: locationSlots?.office ? toPT(parseISO(locationSlots.office)) : null,
          locationUrl: loc.locationUrl
        }
      })
      .filter((loc) => !IGNORE_LOCATIONS_ID.includes(loc.id))
  : []

export const useFetchLocationList = (): {
  data: LocationList
  error: object | null
  isLoading: Boolean
} => {
  const [returnedData, setReturnedData] = useState<LocationList>([])
  const { isLoading, error, data } = useQuery<Response, Error>(
    'locationList',
    () =>
      fetch(
        `${metaEndpoint}?[]serviceArea=MPC&[]serviceArea=MTL&[]serviceArea=WEL`
      ).then((res) => res.json()),
    { cacheTime }
  )

  const {
    isLoading: slotsIsLoading,
    error: slotsError,
    data: slotsData,
  } = useQuery<Array<NextAvailableSlot>, Error>(
    'slots',
    () =>
      axios
        .get(slotsEndpoint)
        .then(
          (res: GetNextAvailableSlotResponse) => res.data.nextAvailableSlotsByLocation
        )
        .catch(() => [])
  )

  useDeepCompareEffect(() => {
    setReturnedData(
      formatData(slotsData || [], data?.MPC)
        .concat(formatData(slotsData || [], data?.WEL))
        .concat(formatData(slotsData || [], data?.MTL))
    )
  }, [data, slotsData || []])

  return {
    isLoading: isLoading || slotsIsLoading,
    error: error && slotsError,
    data: returnedData,
  }
}
